import React, { useState, useMemo } from 'react';
// @ts-ignore
import styles from './MonitoringCenterListItem.module.scss';
import { RequestPriority } from '../../Enums/RequestPriority.enum';
import { RequestState } from '../../Enums/RequestState.enum';
import { Moment } from 'moment';
import { FormattedDateTime } from '../../../../Components/FormattedDate';
import LinkButton from '../../../../Components/Button/LinkButton';
import { Theme } from '../../../../Enums/Theme.enum';
import { showCaseDetail } from '../../../Case/Services/case.service';
import { useAppContext } from '../../../../Services/AppContext';
import OverlayLoading from '../../../../Components/OverlayLoading';
import Button from '../../../../Components/Button/Button';
import PriorityPicker, {
	PriorityPickerValue,
} from '../../../Base/Components/PriorityPicker/PriorityPicker';
import CaseForwarderModal from '../../../Case/Components/CaseForwarder/CaseForwarderModal';
import Text from '../../../../Components/Base/Text/Text';
import Tooltip from '../../../../Components/Popup/Tooltip';
import {
	useAssignCaseMutation,
	usePriorityCaseMutation,
} from '../../../../graphql/graphql';
import CaseNoteButton from '../../../Case/Components/CaseNote/CaseNoteButton';
import useApiClient from '../../../Base/Hooks/useApiClient';
import CaseNotePreview from '../../../Case/Components/CaseNote/CaseNotePreview';
import { CaseItemMedicalReport } from '../../../Case/Interfaces/Case.interface';
import { Backlink } from '../../../Base/Enums/Backlink.enum';
import PatientProfileButtonWithVirtualSelection from '../../../Patient/Components/PatientProfileButtonWithVirtualSelection/PatientProfileButtonWithVirtualSelection';
import CasePermanentMedicationOptionsContextMenu, {
	PermanentMedicationContextMenuTriggerType,
} from '../../../Case/Components/CasePermanentMedicationOptionsContextMenu/CasePermanentMedicationOptionsContextMenu';

export interface IMonitoringCenterListItem {
	id: string;
	number: string;
	priority: RequestPriority;
	state: RequestState;
	caseType: string;
	patient: {
		id: string;
		fullName: string;
		birthNumber: string;
		attendingDoctor: {
			id: string;
			fullName: string;
		};
		originalId?: string | null;
	};
	assignedDoctor?: {
		id: string;
		shortedName: string;
		fullName: string;
	};
	workplace: {
		id: string;
		title: string;
		shortcut: string;
	};
	diagnosis?: {
		id: string;
		title: string;
	};
	note?: {
		id: string;
		text: string;
	};
	medicalReports?: CaseItemMedicalReport[];
	createdAt: Moment;
	lastChangeAt: Moment;
	isNew: boolean;
	isMyCase: boolean;
	isAssigned: boolean;
	totalUnreadCaseEntries?: number;
	caseTypeCode: string;
	medicationLasted?: { value: string; medicationId: string }[];
}

interface Props {
	item: IMonitoringCenterListItem;
	priorityOptions: PriorityPickerValue[];
	onRefresh: () => void;
	workplaceMemberId?: string;
}

const CASE_STATE_TRANSLATIONS: Record<string, string> = {
	new: 'component.monitoringCenter.caseStatus.new',
	inProgress: 'component.monitoringCenter.caseStatus.inProgress',
};

const MonitoringCenterListItem: React.FC<Props> = ({
	item,
	priorityOptions,
	onRefresh,
	workplaceMemberId,
}) => {
	const [isCaseForwarderOpen, setIsCaseForwarderOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const { workplaceId, userData } = useAppContext();
	const [assignCase] = useAssignCaseMutation();
	const [setCasePriority] = usePriorityCaseMutation();
	const { displayError } = useApiClient();

	const handleConnectWithPatient = async (caseId: string) => {
		if (workplaceMemberId) {
			setIsLoading(true);
			try {
				await assignCase({
					caseId,
					workplaceId,
					workplaceMemberId,
				}).unwrap();
				await showCaseDetail(caseId, Backlink.MONITORING_CENTER);
			} catch (e) {
				displayError(e);
				setIsLoading(false);
			}
		}
	};

	const handleVisitCase = async (caseId: string) => {
		await showCaseDetail(caseId, Backlink.MONITORING_CENTER);
	};

	const handleChangePriority = async (
		priorityPickerItem: PriorityPickerValue,
	) => {
		try {
			await setCasePriority({
				input: {
					caseId: item.id,
					priorityId: priorityPickerItem.id,
				},
			}).unwrap();
		} catch (e) {
			displayError(e);
		}
	};

	const handleAfterCaseReAssign = () => {
		setIsCaseForwarderOpen(false);
		onRefresh();
	};

	const isDisabled = !item.isNew && !item.isMyCase;
	const isConnectEnable =
		item.totalUnreadCaseEntries && item.totalUnreadCaseEntries > 0;

	const isPermanentMedication = item.caseTypeCode === 'permanent_medication';

	const label = useMemo(() => {
		if (
			isPermanentMedication &&
			item.medicationLasted &&
			(item.medicationLasted.length || 0) > 0
		) {
			return (
				<Tooltip trigger={item.caseType}>
					{item.medicationLasted.map(item => (
						<div>{item.value}</div>
					))}
				</Tooltip>
			);
		}
		return item.caseType;
	}, [item]);

	return (
		<>
			{isLoading && <OverlayLoading />}
			<tr className={styles.wrapper}>
				<td>
					<LinkButton
						title={item.number}
						theme={Theme.BLUE}
						onClick={() => handleVisitCase(item.id)}
						className={styles.numberButton}
					/>
				</td>
				<td>
					<PriorityPicker
						options={priorityOptions}
						value={
							priorityOptions.find(option => option.key === item.priority) ||
							priorityOptions.find(item => item.key === 'none')
						}
						onPick={handleChangePriority}
					/>
				</td>
				<td>
					<Text tx={CASE_STATE_TRANSLATIONS[item.state]} />
				</td>
				<td className={styles.boldItem}>{label}</td>
				<td>
					<PatientProfileButtonWithVirtualSelection
						patientId={item.patient.id}
						isVirtual={!!item.patient.originalId}
						title={item.patient.fullName}
						className={styles.patientButton}
					/>
				</td>
				<td>{item.patient.birthNumber}</td>
				<td className={styles.boldItem}>
					{item.patient.attendingDoctor.fullName}
				</td>
				{item.assignedDoctor?.id && item.assignedDoctor?.shortedName ? (
					<td>
						<LinkButton
							title={item.assignedDoctor.shortedName}
							theme={Theme.BLUE}
							onClick={() => setIsCaseForwarderOpen(true)}
							className={styles.assignButton}
						/>
					</td>
				) : (
					<td>
						<LinkButton
							title='component.monitoringCenter.notAssigned'
							theme={Theme.RED}
							onClick={() => setIsCaseForwarderOpen(true)}
						/>
					</td>
				)}
				<td>{item.workplace.shortcut}</td>
				<td>{item.diagnosis?.title}</td>
				<td>
					<CaseNotePreview text={item.note?.text} />
					<CaseNoteButton
						note={item.note}
						className={styles.noteButton}
						caseId={item.id}
						onSuccess={onRefresh}
						firstUpper
					/>
				</td>
				<td>
					{item.createdAt && <FormattedDateTime datetime={item.createdAt} />}
				</td>
				<td>
					{item.lastChangeAt && (
						<FormattedDateTime datetime={item.lastChangeAt} />
					)}
				</td>
				{isPermanentMedication && item.medicationLasted ? (
					<td>
						<CasePermanentMedicationOptionsContextMenu
							patientId={item.patient.id}
							caseId={item.id}
							medications={item.medicationLasted?.map(
								item => item.medicationId,
							)}
							reloadCaseList={onRefresh}
							triggerType={PermanentMedicationContextMenuTriggerType.BUTTON}
						/>
					</td>
				) : (
					<>
						{isDisabled ? (
							<td>
								<Tooltip
									trigger={
										<Button
											title='component.monitoringCenter.tookOver'
											theme={Theme.DISABLED}
											small
										/>
									}
									position='bottom center'>
									{item.assignedDoctor?.shortedName}
								</Tooltip>
							</td>
						) : (
							<td>
								<Button
									title={
										item.isMyCase && item.isAssigned
											? 'component.monitoringCenter.continue'
											: 'component.monitoringCenter.connect'
									}
									theme={Theme.GREEN}
									badge={
										isConnectEnable
											? item.totalUnreadCaseEntries?.toString()
											: undefined
									}
									onClick={() => handleConnectWithPatient(item.id)}
									small
								/>
							</td>
						)}
					</>
				)}
			</tr>
			{isCaseForwarderOpen && (
				<CaseForwarderModal
					opened={isCaseForwarderOpen}
					caseId={item.id}
					onSuccess={handleAfterCaseReAssign}
					onClose={() => setIsCaseForwarderOpen(false)}
				/>
			)}
		</>
	);
};

export default MonitoringCenterListItem;
